/**
 * Gemeinsame HTML-Aufbereitung vor dem Einfügen in Outlook (Taskpane „Verwenden“ + Autorun Standardsignatur).
 * Ziel: gleiche Zeilenabstände in Web, Mobil, Neu- und Classic-Outlook.
 */

import { parseHtmlInert } from "./inertHtml";

function appendStyle(existing: string, extra: string): string {
  const e = existing.trim();
  const x = extra.trim();
  if (!x) return e;
  const merged = e ? (e.endsWith(";") ? e : `${e};`) : "";
  return `${merged}${x.endsWith(";") ? x : `${x};`}`;
}

function rgbRgbaToHex(r: number, g: number, b: number, a: number = 1): string {
  r = Math.max(0, Math.min(255, Math.round(r)));
  g = Math.max(0, Math.min(255, Math.round(g)));
  b = Math.max(0, Math.min(255, Math.round(b)));
  a = Math.max(0, Math.min(1, a));
  if (a < 1) {
    r = Math.round(r * a + 255 * (1 - a));
    g = Math.round(g * a + 255 * (1 - a));
    b = Math.round(b * a + 255 * (1 - a));
  }
  return `#${[r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("")}`;
}

function convertRgbRgbaToHexInStyle(style: string): string {
  return style.replace(
    /rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+)\s*)?\)/gi,
    (_, r, g, b, a) => rgbRgbaToHex(Number(r), Number(g), Number(b), a != null ? Number(a) : 1)
  );
}

/** Ersetzt rgb/rgba in style-Attributen durch Hex (Outlook-kompatibel). */
export function convertRgbRgbaToHexInHtml(html: string): string {
  return html.replace(/style="([^"]*)"/g, (_, styleContent) => {
    return `style="${convertRgbRgbaToHexInStyle(styleContent)}"`;
  });
}

/** Ergänzt img width/height aus style, falls als Attribut fehlend. */
export function ensureImageDimensionsForEmail(html: string): string {
  if (!html || typeof document === "undefined") return html || "";
  const div = parseHtmlInert(html);
  div.querySelectorAll("img").forEach((img) => {
    const style = img.getAttribute("style") || "";
    if (!img.hasAttribute("width")) {
      const m = style.match(/\bwidth:\s*(\d+)\s*(?:px)?/);
      if (m) img.setAttribute("width", m[1]);
    }
    if (!img.hasAttribute("height")) {
      const m = style.match(/\bheight:\s*(\d+)\s*(?:px)?/);
      if (m) img.setAttribute("height", m[1]);
    }
  });
  return div.innerHTML;
}

const UNSTYLED_LINK_FALLBACK_COLOR = "#000000";

function styleColorOf(el: Element): string | null {
  const match = (el.getAttribute("style") || "").match(/(?:^|;)\s*color\s*:\s*([^;]+)/i);
  const value = match?.[1]?.trim();
  if (value) return value;
  if (el.tagName === "FONT") {
    const attr = el.getAttribute("color")?.trim();
    if (attr) return attr;
  }
  return null;
}

function isInheritColor(color: string | null): boolean {
  return !!color && color.toLowerCase() === "inherit";
}

function setStyleColor(el: Element, color: string): void {
  const style = el.getAttribute("style") || "";
  const next = /(?:^|;)\s*color\s*:/i.test(style)
    ? style.replace(/((?:^|;)\s*)color\s*:\s*[^;]*/i, `$1color:${color}`)
    : appendStyle(style, `color:${color}`);
  el.setAttribute("style", next);
}

function ancestorExplicitColor(start: Element | null, root: Element): string | null {
  let el: Element | null = start;
  while (el && el !== root && root.contains(el)) {
    const color = styleColorOf(el);
    if (color && !isInheritColor(color)) return color;
    el = el.parentElement;
  }
  return null;
}

/**
 * Outlook färbt <a> über die Hyperlink-Formatvorlage — eine Inline-Farbe am Link allein verliert dagegen,
 * `color: inherit` ebenso. Links mit eigener Farbe bekommen daher eine konkrete Farbe plus einen inneren
 * <span> mit derselben Farbe. Links ohne Farbangabe bleiben im Outlook-Standardstil.
 */
export function enforceExplicitLinkColors(html: string): string {
  if (!html || !canUseDomForHtmlPrep()) return html || "";
  const root = parseHtmlInert(html);
  const doc = root.ownerDocument;
  root.querySelectorAll("a").forEach((anchor) => {
    let color = styleColorOf(anchor);
    if (!color) return;
    if (isInheritColor(color)) {
      color = ancestorExplicitColor(anchor.parentElement, root) ?? UNSTYLED_LINK_FALLBACK_COLOR;
      setStyleColor(anchor, color);
    }
    const hasColoredContent = Array.from(anchor.querySelectorAll("*")).some((el) => {
      const inner = styleColorOf(el);
      return !!inner && !isInheritColor(inner);
    });
    if (hasColoredContent || !anchor.firstChild) return;
    const wrap = doc.createElement("span");
    wrap.setAttribute("style", `color:${color}`);
    while (anchor.firstChild) wrap.appendChild(anchor.firstChild);
    anchor.appendChild(wrap);
  });
  return root.innerHTML;
}

/** Variante ohne DOM (Classic Win32 Autorun) — ohne Auflösung geerbter Farben. */
function enforceExplicitLinkColorsNoDom(html: string): string {
  return html.replace(/<a\b([^>]*)>([\s\S]*?)<\/a>/gi, (full, attrs: string, inner: string) => {
    const style = attrs.match(/\bstyle\s*=\s*"([^"]*)"/i)?.[1];
    if (!style) return full;
    const color = style.match(/(?:^|;)\s*color\s*:\s*([^;]+)/i)?.[1]?.trim();
    if (!color) return full;
    let nextAttrs = attrs;
    let nextColor = color;
    if (isInheritColor(color)) {
      nextColor = UNSTYLED_LINK_FALLBACK_COLOR;
      nextAttrs = attrs.replace(
        /(\bstyle\s*=\s*")([^"]*)(")/i,
        (_m, open: string, value: string, close: string) =>
          open + value.replace(/((?:^|;)\s*)color\s*:\s*[^;]*/i, `$1color:${nextColor}`) + close
      );
    }
    if (/\bcolor\s*:/i.test(inner)) return `<a${nextAttrs}>${inner}</a>`;
    return `<a${nextAttrs}><span style="color:${nextColor}">${inner}</span></a>`;
  });
}

const INLINE_TAGS = new Set(["SPAN", "B", "STRONG", "I", "EM", "U", "A", "FONT"]);

type BlockTextAlign = "left" | "right" | "center" | "justify" | "";

/** Layout-Styles am Block, die beim Merge erhalten bleiben müssen. */
type BlockLayoutStyles = {
  align: BlockTextAlign;
  /** Leer = kein expliziter Zeilenabstand (Default der Pipeline). */
  lineHeight: string;
  /** Leer = kein Hintergrund. */
  background: string;
};

function normalizeTextAlignValue(raw: string | null | undefined): BlockTextAlign {
  const v = (raw || "").trim().toLowerCase();
  if (v === "left" || v === "start") return "left";
  if (v === "right" || v === "end") return "right";
  if (v === "center" || v === "middle") return "center";
  if (v === "justify") return "justify";
  return "";
}

function readCssProp(style: string, prop: string): string {
  const match = style.match(new RegExp(`(?:^|;)\\s*${prop}\\s*:\\s*([^;]+)`, "i"));
  return match?.[1]?.trim() || "";
}

function setCssProp(style: string, prop: string, value: string): string {
  if (!value) return style;
  if (new RegExp(`(?:^|;)\\s*${prop}\\s*:`, "i").test(style)) {
    return style.replace(new RegExp(`((?:^|;)\\s*)${prop}\\s*:\\s*[^;]*`, "i"), `$1${prop}:${value}`);
  }
  return appendStyle(style, `${prop}:${value}`);
}

function normalizeLineHeightValue(raw: string): string {
  return (raw || "").trim().toLowerCase().replace(/\s+/g, "");
}

function normalizeBackgroundValue(style: string): string {
  const bgColor = readCssProp(style, "background-color");
  const bg = readCssProp(style, "background");
  const v = (bgColor || bg).trim().toLowerCase().replace(/\s+/g, "");
  if (!v || v === "transparent" || v === "rgba(0,0,0,0)" || v === "initial" || v === "inherit") return "";
  return v;
}

/** text-align aus style bzw. HTML-align (Outlook/Word). */
function blockTextAlign(el: Element): BlockTextAlign {
  const style = el.getAttribute("style") || "";
  const fromStyle = readCssProp(style, "text-align");
  return normalizeTextAlignValue(fromStyle) || normalizeTextAlignValue(el.getAttribute("align"));
}

function blockTextAlignFromOpenTag(attrs: string): BlockTextAlign {
  const style = attrs.match(/\bstyle\s*=\s*"([^"]*)"/i)?.[1] || "";
  const fromStyle = readCssProp(style, "text-align");
  const fromAlign = attrs.match(/\balign\s*=\s*"([^"]*)"/i)?.[1];
  return normalizeTextAlignValue(fromStyle) || normalizeTextAlignValue(fromAlign);
}

function readBlockLayoutStyles(el: Element): BlockLayoutStyles {
  const style = el.getAttribute("style") || "";
  return {
    align: blockTextAlign(el),
    lineHeight: normalizeLineHeightValue(readCssProp(style, "line-height")),
    background: normalizeBackgroundValue(style),
  };
}

function readBlockLayoutStylesFromAttrs(attrs: string): BlockLayoutStyles {
  const style = attrs.match(/\bstyle\s*=\s*"([^"]*)"/i)?.[1] || "";
  return {
    align: blockTextAlignFromOpenTag(attrs),
    lineHeight: normalizeLineHeightValue(readCssProp(style, "line-height")),
    background: normalizeBackgroundValue(style),
  };
}

function blockLayoutKey(layout: BlockLayoutStyles): string {
  return `${layout.align}|${layout.lineHeight}|${layout.background}`;
}

function applyBlockTextAlign(el: HTMLElement, align: BlockTextAlign): void {
  if (!align) return;
  el.setAttribute("style", setCssProp(el.getAttribute("style") || "", "text-align", align));
  el.setAttribute("align", align);
}

/**
 * Basis-Kompakt-Styles + erhaltene Layout-Styles (Align, Zeilenabstand, Hintergrund).
 * Bild-Ausrichtung am Parent = text-align; am <img> selbst bleiben margin/display unberührt.
 */
function applyMergedBlockStyles(el: HTMLElement, baseStyle: string, layout: BlockLayoutStyles): void {
  let style = baseStyle;
  if (layout.lineHeight) {
    style = setCssProp(style, "line-height", layout.lineHeight);
  }
  if (layout.background) {
    style = setCssProp(style, "background-color", layout.background);
  }
  el.setAttribute("style", style);
  applyBlockTextAlign(el, layout.align);
}

function isSimpleTextBlock(el: Element): boolean {
  const tag = el.tagName;
  if (tag !== "DIV" && tag !== "P") return false;
  for (const child of Array.from(el.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) continue;
    if (child.nodeType !== Node.ELEMENT_NODE) return false;
    const ctag = (child as Element).tagName;
    if (ctag === "BR") continue;
    if (INLINE_TAGS.has(ctag)) continue;
    return false;
  }
  return true;
}

/**
 * Editor erzeugt oft pro Zeile ein eigenes div/p — Outlook vergibt dann Absatzabstand.
 * Aufeinanderfolgende einfache Blöcke werden zu einem Block mit &lt;br&gt; zusammengeführt.
 * Nur bei gleichem Layout (Align, Zeilenabstand, Hintergrund); sonst gehen Styles verloren.
 */
export function flattenAdjacentSimpleBlocks(html: string): string {
  if (!html || typeof document === "undefined") return html || "";
  const root = parseHtmlInert(html);
  const doc = root.ownerDocument;

  const mergeRunsIn = (container: HTMLElement): void => {
    const nodes = Array.from(container.childNodes);
    let i = 0;
    while (i < nodes.length) {
      const node = nodes[i];
      if (node.nodeType !== Node.ELEMENT_NODE) {
        i++;
        continue;
      }
      const el = node as HTMLElement;
      if (!isSimpleTextBlock(el)) {
        mergeRunsIn(el);
        i++;
        continue;
      }
      const runLayout = readBlockLayoutStyles(el);
      const runKey = blockLayoutKey(runLayout);
      const run: HTMLElement[] = [el];
      let j = i + 1;
      while (j < nodes.length) {
        const next = nodes[j];
        if (next.nodeType !== Node.ELEMENT_NODE) break;
        const nextEl = next as HTMLElement;
        if (!isSimpleTextBlock(nextEl)) break;
        if (blockLayoutKey(readBlockLayoutStyles(nextEl)) !== runKey) break;
        run.push(nextEl);
        j++;
      }
      if (run.length > 1) {
        const merged = doc.createElement("div");
        applyMergedBlockStyles(merged, "margin:0;padding:0;line-height:normal", runLayout);
        run.forEach((block, idx) => {
          if (idx > 0) merged.appendChild(doc.createElement("br"));
          while (block.firstChild) merged.appendChild(block.firstChild);
          block.remove();
        });
        container.insertBefore(merged, nodes[j] ?? null);
        return mergeRunsIn(container);
      }
      i = j;
    }
  };

  mergeRunsIn(root);
  return root.innerHTML;
}

/**
 * Outlook/Word (Classic) respektiert oft HTML-align zuverlässiger als nur CSS text-align.
 * Spiegelt vorhandenes text-align auf das align-Attribut (und umgekehrt).
 */
export function mirrorTextAlignForOutlook(html: string): string {
  if (!html || !canUseDomForHtmlPrep()) return html || "";
  const root = parseHtmlInert(html);
  root.querySelectorAll("div, p, td, th, h1, h2, h3, h4, h5, h6").forEach((el) => {
    const align = blockTextAlign(el);
    if (!align) return;
    applyBlockTextAlign(el as HTMLElement, align);
  });
  return root.innerHTML;
}

function mirrorTextAlignForOutlookNoDom(html: string): string {
  return html.replace(/<(div|p|td|th|h[1-6])\b([^>]*)>/gi, (full, tag: string, attrs: string) => {
    const align = blockTextAlignFromOpenTag(attrs);
    if (!align) return full;
    let nextAttrs = attrs;
    const styleMatch = nextAttrs.match(/\bstyle\s*=\s*"([^"]*)"/i);
    if (styleMatch) {
      const style = styleMatch[1];
      const nextStyle = /(?:^|;)\s*text-align\s*:/i.test(style)
        ? style.replace(/((?:^|;)\s*)text-align\s*:\s*[^;]*/i, `$1text-align:${align}`)
        : appendStyle(style, `text-align:${align}`);
      nextAttrs = nextAttrs.replace(/\bstyle\s*=\s*"[^"]*"/i, `style="${nextStyle}"`);
    } else {
      nextAttrs = `${nextAttrs} style="text-align:${align}"`;
    }
    if (/\balign\s*=/i.test(nextAttrs)) {
      nextAttrs = nextAttrs.replace(/\balign\s*=\s*"[^"]*"/i, `align="${align}"`);
    } else {
      nextAttrs = `${nextAttrs} align="${align}"`;
    }
    return `<${tag}${nextAttrs}>`;
  });
}

/** margin/padding/line-height auf block-level Tags kompakt setzen (Signatur-Slot / MsoNormal). */
export function tightenOutlookBlockSpacing(html: string): string {
  if (!html || typeof document === "undefined") return html || "";
  const root = parseHtmlInert(html);
  root.querySelectorAll("p, div, li").forEach((el) => {
    const cur = el.getAttribute("style") || "";
    let next = cur;
    if (!/\bmargin\s*:/i.test(next)) next = appendStyle(next, "margin:0");
    if (!/\bpadding\s*:/i.test(next)) next = appendStyle(next, "padding:0");
    if (!/\bline-height\s*:/i.test(next)) next = appendStyle(next, "line-height:normal");
    if (next) el.setAttribute("style", next);
  });
  return root.innerHTML;
}

/** Ersetzt font-size: Xpx in style-Attributen durch pt (Outlook). */
export function convertFontSizePxToPtInHtml(html: string): string {
  return html.replace(/style="([^"]*)"/g, (_, styleContent) => {
    const converted = styleContent.replace(/\bfont-size:\s*(\d+)\s*px\b/gi, "font-size: $1pt");
    return `style="${converted}"`;
  });
}

function canUseDomForHtmlPrep(): boolean {
  try {
    return typeof document !== "undefined" && typeof document.createElement === "function";
  } catch {
    return false;
  }
}

function ensureImageDimensionsForEmailRegex(html: string): string {
  return html.replace(/<img\s+([^>]*?)>/gi, (_full, inner: string) => {
    const hasWidth = /\bwidth\s*=/i.test(inner);
    const hasHeight = /\bheight\s*=/i.test(inner);
    const style = inner.match(/\bstyle\s*=\s*"([^"]*)"/i)?.[1] || "";
    const wM = style.match(/\bwidth:\s*(\d+)\s*(?:px)?/i);
    const hM = style.match(/\bheight:\s*(\d+)\s*(?:px)?/i);
    let out = inner;
    if (!hasWidth && wM) out = `width="${wM[1]}" ${out}`;
    if (!hasHeight && hM) out = `height="${hM[1]}" ${out}`;
    return `<img ${out}>`;
  });
}

function isInsideOpenTable(html: string, offset: number): boolean {
  const before = html.slice(0, offset);
  const lastOpen = before.lastIndexOf("<table");
  const lastClose = before.lastIndexOf("</table>");
  return lastOpen !== -1 && lastOpen > lastClose;
}

const BLOCK_GAP_RE = /<\/(div|p)>\s*<(div|p)\b[^>]*>/gi;

function shouldMergeAdjacentBlockGap(html: string, offset: number, match: string): boolean {
  if (isInsideOpenTable(html, offset)) return false;
  const after = html.slice(offset + match.length);
  if (/^<(div|p)\b[^>]*\bclass\s*=\s*"[^"]*sunnysign-campaign/i.test(after)) return false;
  const before = html.slice(0, offset);
  const openStart = Math.max(before.lastIndexOf("<div"), before.lastIndexOf("<p"));
  if (openStart === -1) return false;
  const openTagEnd = before.indexOf(">", openStart);
  if (openTagEnd === -1) return false;
  const openTag = before.slice(openStart, openTagEnd + 1);
  const openAttrs = openTag.replace(/^<(?:div|p)\b/i, "").replace(/>$/, "");
  const nextOpen = match.match(/<(div|p)\b([^>]*)>/i);
  const nextAttrs = nextOpen?.[2] || "";
  if (
    blockLayoutKey(readBlockLayoutStylesFromAttrs(openAttrs)) !==
    blockLayoutKey(readBlockLayoutStylesFromAttrs(nextAttrs))
  ) {
    return false;
  }
  const inner = before.slice(openTagEnd + 1, offset);
  if (/<(?:div|p|table|img)\b/i.test(inner)) return false;
  return true;
}

/** div/p-Zeilen zusammenführen ohne DOM (Classic-Autorun JS-Runtime). */
function mergeAdjacentBlockTagsWithBrNoDom(html: string): string {
  let out = html || "";
  let prev = "";
  while (out !== prev) {
    prev = out;
    out = out.replace(BLOCK_GAP_RE, (match, _c1, _c2, offset, whole) => {
      return shouldMergeAdjacentBlockGap(whole, offset, match) ? "<br>" : match;
    });
  }
  return out;
}

function processHtmlSegmentsOutsideTables(html: string, fn: (segment: string) => string): string {
  const parts = html.split(/(<table\b[^>]*>[\s\S]*?<\/table>)/gi);
  return parts.map((part, i) => (i % 2 === 1 ? part : fn(part))).join("");
}

function appendStylesToBlockTagsRegex(
  html: string,
  rules: Array<{ prop: RegExp; value: string }>
): string {
  return html.replace(/<(div|p|td|li)\b([^>]*)>/gi, (full, tag: string, attrs: string) => {
    const styleMatch = attrs.match(/\bstyle\s*=\s*"([^"]*)"/i);
    let style = styleMatch ? styleMatch[1] : "";
    for (const { prop, value } of rules) {
      if (!prop.test(style)) style = appendStyle(style, value);
    }
    if (!style) return full;
    if (styleMatch) {
      return full.replace(/style\s*=\s*"[^"]*"/i, `style="${style}"`);
    }
    return `<${tag}${attrs} style="${style}">`;
  });
}

/**
 * HTML-Aufbereitung ohne DOM — nur für Classic Win32 Autorun (lädt autorunweb.js ohne HTML-Seite).
 * Web/Neu/Mobil Autorun und Taskpane nutzen die DOM-Varianten unten.
 */
function prepareSignatureHtmlForOutlookComposeNoDom(html: string): string {
  let out = html || "";
  out = convertRgbRgbaToHexInHtml(out);
  out = convertFontSizePxToPtInHtml(out);
  out = ensureImageDimensionsForEmailRegex(out);
  out = enforceExplicitLinkColorsNoDom(out);
  out = processHtmlSegmentsOutsideTables(out, mergeAdjacentBlockTagsWithBrNoDom);
  out = appendStylesToBlockTagsRegex(out, [
    { prop: /\bmargin\s*:/i, value: "margin:0" },
    { prop: /\bpadding\s*:/i, value: "padding:0" },
    { prop: /\bline-height\s*:/i, value: "line-height:normal" },
  ]);
  out = mirrorTextAlignForOutlookNoDom(out);
  return out;
}

/** Classic Win32 Kompaktierung ohne DOM (nur Autorun JS-Runtime). */
function compactSignatureHtmlForOutlookClassicWin32NoDom(html: string): string {
  let out = processHtmlSegmentsOutsideTables(html || "", mergeAdjacentBlockTagsWithBrNoDom);
  out = appendStylesToBlockTagsRegex(out, [
    { prop: /\bmargin\s*:/i, value: "margin:0" },
    { prop: /\bmso-margin-top-alt\s*:/i, value: "mso-margin-top-alt:0" },
    { prop: /\bmso-margin-bottom-alt\s*:/i, value: "mso-margin-bottom-alt:0" },
    { prop: /\bpadding\s*:/i, value: "padding:0" },
    { prop: /\bline-height\s*:/i, value: "line-height:115%" },
    { prop: /\bmso-line-height-rule\s*:/i, value: "mso-line-height-rule:exactly" },
  ]);
  return out;
}

/** Einheitliche Aufbereitung für Taskpane und Autorun vor body.setAsync / Merge. */
export function prepareSignatureHtmlForOutlookCompose(html: string): string {
  let out = html || "";
  out = convertRgbRgbaToHexInHtml(out);
  out = convertFontSizePxToPtInHtml(out);
  if (canUseDomForHtmlPrep()) {
    out = ensureImageDimensionsForEmail(out);
    out = enforceExplicitLinkColors(out);
    out = flattenAdjacentSimpleBlocks(out);
    out = tightenOutlookBlockSpacing(out);
    out = mirrorTextAlignForOutlook(out);
  }
  return out;
}

const CLASSIC_MS_WORD_COMPACT_STYLE =
  "margin:0;padding:0;mso-margin-top-alt:0;mso-margin-bottom-alt:0;line-height:115%;mso-line-height-rule:exactly";

/** Style für Classic-Autorun-Fragment-Wrapper (export für compose-runtime). */
export const CLASSIC_MS_WORD_FRAGMENT_STYLE = CLASSIC_MS_WORD_COMPACT_STYLE;

const CLASSIC_BLOCK_TAGS = new Set(["DIV", "P"]);

function isClassicMergeableBlock(el: Element): boolean {
  return CLASSIC_BLOCK_TAGS.has(el.tagName);
}

/** Bild-/Tabellen-Wrapper nicht mergen — Parent-Align und Bild-margin bleiben unangetastet. */
function classicBlockHasMedia(el: Element): boolean {
  return !!el.querySelector("img, table");
}

/**
 * Outlook Classic (Win32 / Word-Editor): jeder Block-level div/p wird oft zu MsoNormal mit Absatzabstand.
 * Aufeinanderfolgende div/p werden zu einem Block mit &lt;br&gt; zusammengeführt; MSO-kompakte Styles setzen.
 * Nur bei gleichem Layout (Align, Zeilenabstand, Hintergrund); Medien-Wrapper bleiben getrennt.
 * Nur für Classic — Web/Neu/Mobil unverändert.
 */
export function compactSignatureHtmlForOutlookClassicWin32(html: string): string {
  if (!html || !canUseDomForHtmlPrep()) return html || "";
  const root = parseHtmlInert(html);
  const doc = root.ownerDocument;

  const mergeRunsIn = (container: HTMLElement): void => {
    let mergedAny = false;
    const nodes = Array.from(container.childNodes);
    let i = 0;
    while (i < nodes.length) {
      const node = nodes[i];
      if (node.nodeType !== Node.ELEMENT_NODE) {
        i++;
        continue;
      }
      const el = node as HTMLElement;
      if (!isClassicMergeableBlock(el) || classicBlockHasMedia(el)) {
        mergeRunsIn(el);
        i++;
        continue;
      }
      const runLayout = readBlockLayoutStyles(el);
      const runKey = blockLayoutKey(runLayout);
      const run: HTMLElement[] = [el];
      let j = i + 1;
      while (j < nodes.length) {
        const next = nodes[j];
        if (next.nodeType !== Node.ELEMENT_NODE) break;
        const nextEl = next as HTMLElement;
        if (!isClassicMergeableBlock(nextEl) || classicBlockHasMedia(nextEl)) break;
        if (blockLayoutKey(readBlockLayoutStyles(nextEl)) !== runKey) break;
        run.push(nextEl);
        j++;
      }
      if (run.length > 1) {
        mergedAny = true;
        const merged = doc.createElement("div");
        applyMergedBlockStyles(merged, CLASSIC_MS_WORD_COMPACT_STYLE, runLayout);
        run.forEach((block, idx) => {
          if (idx > 0) merged.appendChild(doc.createElement("br"));
          while (block.firstChild) merged.appendChild(block.firstChild);
          block.remove();
        });
        container.insertBefore(merged, nodes[j] ?? null);
        break;
      }
      mergeRunsIn(el);
      i = j;
    }
    if (mergedAny) mergeRunsIn(container);
  };

  mergeRunsIn(root);

  root.querySelectorAll("p,div,td").forEach((el) => {
    const cur = el.getAttribute("style") || "";
    let next = cur;
    if (!/\bmargin\s*:/i.test(next)) next = appendStyle(next, "margin:0");
    if (!/\bmso-margin-top-alt\s*:/i.test(next)) next = appendStyle(next, "mso-margin-top-alt:0");
    if (!/\bmso-margin-bottom-alt\s*:/i.test(next)) next = appendStyle(next, "mso-margin-bottom-alt:0");
    if (!/\bpadding\s*:/i.test(next)) next = appendStyle(next, "padding:0");
    if (!/\bline-height\s*:/i.test(next)) next = appendStyle(next, "line-height:115%");
    if (!/\bmso-line-height-rule\s*:/i.test(next)) next = appendStyle(next, "mso-line-height-rule:exactly");
    el.setAttribute("style", next);
  });

  return root.innerHTML;
}

/** Marker-Attribut für Classic-Autorun-Dedup (Antwort/Weiterleitung). */
export const OUTLOOK_CLASSIC_SIG_MARKER = 'data-sunnysign-sig="1"';

/**
 * Gleiche Pipeline wie Taskpane „Verwenden“ auf Classic Win32 (DOM in taskpane.html).
 */
export function prepareClassicOutlookSignatureInsert(
  html: string,
  appendWatermark: (h: string) => string
): string {
  let out = prepareSignatureHtmlForOutlookCompose(html);
  out = compactSignatureHtmlForOutlookClassicWin32(out);
  return appendWatermark(out);
}

/**
 * Nur Classic Win32 **Autorun** (OnNewMessageCompose, lädt nur autorunweb.js ohne DOM).
 * Web / Neu-Outlook / Mobil Autorun bleiben bei processSignatureHtml unverändert.
 */
export function prepareClassicOutlookAutorunSignatureInsert(
  html: string,
  appendWatermark: (h: string) => string
): string {
  let out = prepareSignatureHtmlForOutlookComposeNoDom(html);
  out = compactSignatureHtmlForOutlookClassicWin32NoDom(out);
  return appendWatermark(out);
}

/**
 * Unsichtbarer Marker für Autorun-Dedup — kein Wrapper-&lt;div&gt; (vermeidet Word-Absatzabstände in Classic).
 */
export function withClassicOutlookSignatureMarker(html: string): string {
  if (html.includes(OUTLOOK_CLASSIC_SIG_MARKER)) return html;
  return `<span style="display:none;mso-hide:all;font-size:0;line-height:0;max-height:0;overflow:hidden" ${OUTLOOK_CLASSIC_SIG_MARKER}></span>${html}`;
}
